import {
  Box,
  Card,
  Stack,
  Table,
  Button,
  Divider,
  TableRow,
  Container,
  TableBody,
  TableCell,
  TextField,
  Typography,
  Autocomplete,
  TableContainer,
} from '@mui/material';

import { useState, useEffect, useCallback } from 'react';
import { useBoolean } from 'src/hooks/use-boolean';
import { RouterLink } from 'src/routes/components';
import { Pagination } from 'src/type/utils';
import axios, { endpoints } from 'src/utils/axios';
import { toastError } from 'src/utils/format-error';
import { fParams, ParamsType } from 'src/utils/format-params';
import usePagination from 'src/hooks/use-pagination';
import Scrollbar from 'src/components/scrollbar';
import TableEmptyRows from 'src/components/table/table-empty-rows';
import TableHeadCustom from 'src/components/table/table-head-custom';
import TableLoading from 'src/components/table/table-loading';
import TableNoData from 'src/components/table/table-no-data';
import TablePaginationCustom from 'src/components/table/table-pagination-custom';
import { emptyRows } from 'src/components/table/utils';
import { fNumber } from 'src/utils/format-number';
import Iconify from 'src/components/iconify';
import { paths } from 'src/routes/paths';
import { Stock } from 'src/type/stocks';
import { ProductRedeemFee } from 'src/type/gold-withdraw';
// import { convertGold } from 'src/utils/convert';
import ProductSummarySelector from '../components/product-summary-selector';

const tableHead = [
  { id: 'productName', label: 'รายการทั้งหมด' },
  { id: 'weightGram', label: 'นน. (กรัม)', width: 120, align: 'right' },
  { id: 'weightBaht', label: 'นน. (บาท)', width: 120, align: 'right' },
  { id: 'amount', label: 'จำนวน (ปัจจุบัน)', width: 150, align: 'right' },
  // { id: 'preRemainGoldAmount', label: 'pre', width: 130, align: 'right' },
  { id: 'priceRange', label: 'ช่วงราคา/บาท', width: 180, align: 'center' },
  // { id: 'priceGram', label: 'ราคา/กรัม', width: 120, align: 'right' },
];

export default function StockView() {
  const initialize = useBoolean(true);
  const reload = useBoolean(false);
  const loadingProductRedeemFees = useBoolean(false);

  const [productRedeemFees, setProductRedeemFees] = useState<ProductRedeemFee[]>([]);
  const [selectedProductRedeemFee, setSelectedProductRedeemFee] = useState<ProductRedeemFee | null>(
    null
  );
  const [selectedProductId, setSelectedProductId] = useState<string | null>(null);

  const {
    currentPage,
    prevPage,
    rowsPerPage,
    meta,
    onSetData,
    onChangePage,
    onChangeRowsPerPage,
    onResetPage,
    data: tableData,
  } = usePagination<Stock>({ defaultRowsPerPage: 10 });

  // Fetch product-redeem-fees list by productId
  const handleGetProductRedeemFees = useCallback(async (productId: string) => {
    try {
      loadingProductRedeemFees.onTrue();
      const { data } = await axios.get<Pagination<ProductRedeemFee>>(
        endpoints.productRedeemFees.get,
        {
          params: { limit: 200, productId },
        }
      );
      // Sort by weightGram ascending
      const sortedResults = data.results.sort((a, b) => a.weightGram - b.weightGram);
      setProductRedeemFees(sortedResults);
      // Reset selected product-redeem-fee when product changes
      setSelectedProductRedeemFee(null);
    } catch (error) {
      toastError(error);
    } finally {
      loadingProductRedeemFees.onFalse();
    }
  }, []);

  // Handle product change from ProductSummarySelector
  const handleProductChange = useCallback(
    (productId: string) => {
      setSelectedProductId(productId);
      handleGetProductRedeemFees(productId);
      onResetPage();
    },
    [handleGetProductRedeemFees, onResetPage]
  );

  // Handle filter change
  const handleFilterChange = useCallback(
    (newValue: ProductRedeemFee | null) => {
      setSelectedProductRedeemFee(newValue);
      onResetPage();
    },
    [onResetPage]
  );

  useEffect(() => {
    if (!selectedProductId) return;
    const pageDirection = currentPage > prevPage ? 'next' : 'prev';
    const params = {
      limit: rowsPerPage,
      productId: selectedProductId,
      ...(pageDirection === 'next' && { lastDocId: meta.lastDocId }),
      ...(pageDirection === 'prev' && { firstDocId: meta.firstDocId }),
      ...(selectedProductRedeemFee && { productRedeemFeeId: selectedProductRedeemFee.id }),
    };
    handleGetTx(params);
  }, [currentPage, rowsPerPage, reload.value, selectedProductRedeemFee, selectedProductId]);

  const handleGetTx = useCallback(async (params: ParamsType) => {
    try {
      initialize.onTrue();
      const { data } = await axios.get<Pagination<Stock>>(endpoints.stockFactories.get, {
        params: fParams(params),
      });
      onSetData(data);
    } catch (error) {
      toastError(error);
    } finally {
      initialize.onFalse();
    }
  }, []);

  return (
    <Container>
      <Stack spacing={3}>
        <Stack direction="row" justifyContent="space-between">
          <Typography variant="h4" gutterBottom>
            สต็อกโรงงาน
          </Typography>

          <Stack direction="row" spacing={2}>
            <Button
              component={RouterLink}
              href={paths.stock.import}
              variant="contained"
              startIcon={<Iconify icon="mingcute:add-line" />}
            >
              รับเข้า
            </Button>
            <Button
              component={RouterLink}
              href={paths.stock.export}
              variant="contained"
              startIcon={<Iconify icon="ic:baseline-minus" />}
            >
              จ่ายออก
            </Button>
          </Stack>
        </Stack>

        <ProductSummarySelector onProductChange={handleProductChange} />

        <Divider />
        <Card>
          {/* Filter by product-redeem-fees */}
          <Stack direction="row" spacing={2} alignItems="center" sx={{ p: 2 }}>
            <Typography variant="subtitle2" sx={{ minWidth: 80 }}>
              กรองตาม:
            </Typography>
            <Autocomplete
              sx={{ minWidth: 300 }}
              options={productRedeemFees}
              getOptionLabel={(option) => option.name}
              value={selectedProductRedeemFee}
              onChange={(_, newValue) => handleFilterChange(newValue)}
              loading={loadingProductRedeemFees.value}
              disabled={loadingProductRedeemFees.value}
              noOptionsText="ไม่พบรายการ"
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="รายการทั้งหมด"
                  placeholder="เลือกรายการ"
                  variant="outlined"
                  size="small"
                />
              )}
              renderOption={(props, option) => (
                <Box component="li" {...props} key={option.id}>
                  <Stack direction="row" spacing={1} alignItems="center" sx={{ width: '100%' }}>
                    <Typography variant="body2" sx={{ flex: 1 }}>
                      {option.name}
                    </Typography>
                  </Stack>
                </Box>
              )}
            />
            {selectedProductRedeemFee && (
              <Button
                size="small"
                color="inherit"
                onClick={() => handleFilterChange(null)}
                startIcon={<Iconify icon="mdi:close" />}
              >
                ล้าง
              </Button>
            )}
          </Stack>

          <Divider />

          <TableContainer sx={{ position: 'relative', overflow: 'unset' }}>
            <Scrollbar>
              <Table size="small">
                <TableHeadCustom headLabel={tableHead} />

                <TableBody>
                  <TableLoading loading={initialize.value} rows={rowsPerPage}>
                    {tableData.map((row) => (
                      <TableRow key={row.id}>
                        <TableCell sx={{ textWrap: 'nowrap' }}>
                          {row.productRedeemFee?.name || '-'}
                        </TableCell>
                        <TableCell align="right" sx={{ minWidth: 110 }}>
                          {fNumber(Math.ceil(row.weightGram * 1000) / 1000, 3)}
                        </TableCell>
                        <TableCell align="right" sx={{ minWidth: 110 }}>
                          {fNumber(row.weightBaht, 4)}
                        </TableCell>
                        <TableCell align="right" sx={{ minWidth: 110 }}>
                          {fNumber(row.amount, 4)}
                        </TableCell>
                        {/* <TableCell align="right" sx={{ minWidth: 110 }}>
                          {fNumber(row.preRemainGoldAmount || 0, 4)}
                        </TableCell> */}
                        <TableCell align="center" sx={{ minWidth: 110 }}>
                          {fNumber(row.priceMin, 0)} - {fNumber(row.priceMax, 0)}
                        </TableCell>
                        {/* <TableCell align="right" sx={{ minWidth: 110 }}>
                          {fNumber(convertGold(row.price, 'priceGrams'), 4)}
                        </TableCell> */}
                      </TableRow>
                    ))}

                    <TableEmptyRows
                      show={!initialize.value}
                      height={35}
                      emptyRows={emptyRows(currentPage, rowsPerPage, meta.totalResults)}
                    />

                    <TableNoData notFound={!tableData.length} />
                  </TableLoading>
                </TableBody>
              </Table>
            </Scrollbar>
          </TableContainer>

          <TablePaginationCustom
            size="small"
            count={meta.totalResults}
            page={currentPage}
            rowsPerPage={rowsPerPage}
            onPageChange={onChangePage}
            onRowsPerPageChange={onChangeRowsPerPage}
          />
        </Card>
      </Stack>
    </Container>
  );
}
